import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import IntegrityPanel from "../components/IntegrityPanel";
import StudioPublishPanel from "../components/studio/StudioPublishPanel";
import JobMatchesPanel from "../components/JobMatchesPanel";
import {
  Wand2, Loader, Check, FileText, Download, ArrowRight, ArrowLeft, Sparkles,
  AlertTriangle, Info, Save, Eye, Plus, Gauge, ChevronDown, ChevronUp,
  ShieldCheck, User, Mail, Phone, MapPin, Linkedin, Store, Zap, X, Send,
} from "lucide-react";

export default function Studio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // ?analysis_id=<id> tells the Studio which analysis to pull suggestions for
  // — set by the Dashboard's "Open Studio" CTA so the user doesn't have to
  // re-paste the JD they just analyzed.
  const requestedAnalysisId = new URLSearchParams(location.search).get("analysis_id") || "";
  const focusParam = new URLSearchParams(location.search).get("focus") || "";
  const [focusDismissed, setFocusDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [content, setContent] = useState("");
  const [templateId, setTemplateId] = useState("classic_chronological");
  const [applied, setApplied] = useState(new Set());
  const [saveStatus, setSaveStatus] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [toast, setToast] = useState(null);
  const [ats, setAts] = useState(null);
  const [atsLoading, setAtsLoading] = useState(false);
  // Persisted per-user contact header (name/email/phone/linkedin/location).
  // Defaults from the user's account (name+email) and is editable per-download.
  const [contactInfo, setContactInfo] = useState({
    name: "", email: "", phone: "", linkedin_url: "", location: "",
  });
  const [contactInfoLoaded, setContactInfoLoaded] = useState(false);
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  const [contactSaveStatus, setContactSaveStatus] = useState("");
  // Session-scoped replay buffer. Whenever a tweak is applied this session
  // we snapshot the "before" content once (the session baseline) and push the
  // suggestion id to applyOrder. Undo can then re-derive the content by
  // replaying the remaining ids on top of the baseline — so the ATS score
  // moves back down correctly when a tweak is undone.
  const [sessionBaseline, setSessionBaseline] = useState(null);
  const [applyOrder, setApplyOrder] = useState([]);
  // Ref to the resume <textarea> so we can imperatively scroll + select the
  // section a suggestion is talking about. The user requested that opening
  // a tweak in the left rail visually highlights the corresponding area of
  // the resume on the right — we do that by selecting the section's text.
  const editorRef = useRef(null);
  // Section currently highlighted (key = lowercase section name). Drives the
  // pulsing yellow border around the editor when a tweak is opened.
  const [focusedSection, setFocusedSection] = useState(null);
  // Job connection: keeps the user tethered to the job they came from so
  // they can re-check fit and apply without hunting through History.
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [recheckError, setRecheckError] = useState("");
  const [applyNudge, setApplyNudge] = useState(false);
  const [appliedToJob, setAppliedToJob] = useState(false);

  /**
   * Scroll the editor textarea to the section heading (e.g. "## Experience")
   * matching the supplied section label, and select all of its content so the
   * browser's native selection highlight shows the user "this is the area
   * the tweak refers to." If the section doesn't exist yet (e.g. the tweak
   * suggests adding it), we scroll to the top instead.
   */
  const focusSection = (section) => {
    if (!section) return;
    const ta = editorRef.current;
    if (!ta) return;
    const text = ta.value || "";
    // Headings in our templates look like "## Experience". Match
    // case-insensitively and allow trailing decoration on the line.
    const headingRe = new RegExp(`^##\\s+${section}\\b.*$`, "im");
    const m = text.match(headingRe);
    if (!m) {
      // Section doesn't exist — flash the whole editor to nudge the user.
      ta.focus({ preventScroll: false });
      ta.setSelectionRange(0, 0);
      ta.scrollTop = 0;
      setFocusedSection(section.toLowerCase());
      setTimeout(() => setFocusedSection(null), 1600);
      return;
    }
    const start = m.index;
    // End at the next "## " heading or end-of-doc.
    const after = text.slice(start + m[0].length);
    const nextRe = /\n##\s+/;
    const nextMatch = after.match(nextRe);
    const end = nextMatch ? start + m[0].length + nextMatch.index : text.length;

    ta.focus({ preventScroll: true });
    ta.setSelectionRange(start, end);
    // Scroll the textarea so the heading is near the top.
    // Approximate line-height from font-size via computed style.
    const cs = window.getComputedStyle(ta);
    const lh = parseFloat(cs.lineHeight) || 22;
    const linesBeforeStart = text.slice(0, start).split("\n").length - 1;
    ta.scrollTop = Math.max(0, linesBeforeStart * lh - 24);
    setFocusedSection(section.toLowerCase());
    setTimeout(() => setFocusedSection(null), 1600);
  };

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const url = requestedAnalysisId
          ? `/studio/state?analysis_id=${encodeURIComponent(requestedAnalysisId)}`
          : "/studio/state";
        const { data } = await api.get(url);
        setState(data);
        const draft = data.draft;
        setContent(draft?.content_md || data.resume_text || "");
        setTemplateId(draft?.template_id || "classic_chronological");
        if (Array.isArray(draft?.applied_ids) && draft.applied_ids.length) {
          setApplied(new Set(draft.applied_ids));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate, requestedAnalysisId]);

  // Load persisted contact-info (defaults from account name+email if never set)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/studio/contact");
        setContactInfo({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          linkedin_url: data.linkedin_url || "",
          location: data.location || "",
        });
      } catch {
        // fall back to whatever the AuthContext gave us
        setContactInfo((prev) => ({
          ...prev,
          name: prev.name || user.name || "",
          email: prev.email || user.email || "",
        }));
      } finally {
        setContactInfoLoaded(true);
      }
    })();
  }, [user]);

  // Live ATS score — debounced 700ms after the last keystroke
  useEffect(() => {
    if (!content) {
      setAts(null);
      return;
    }
    setAtsLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/studio/score", { content_md: content });
        setAts(data);
      } catch {
        // silent fail — score badge is non-blocking
      } finally {
        setAtsLoading(false);
      }
    }, 700);
    return () => clearTimeout(handle);
  }, [content]);

  // Smart patcher — actually mutates the resume content so the ATS score moves
  const PATCH_TEMPLATES = {
    "heur-no-summary": (c) =>
      addSection(c, "Summary",
        "Senior product manager with 8 years scaling B2B SaaS — led the launch of X reaching $5M ARR in 12 months across 3 enterprise verticals.",
        { atTop: true }),
    "heur-no-skills": (c) =>
      addSection(c, "Skills",
        "Python · SQL · React · TypeScript · AWS · Docker · Project Management · Stakeholder Communication · Data Analysis · Agile · Mentorship · Cross-functional Leadership"),
    "heur-no-bullets": (c) => convertParagraphsToBullets(c),
    "heur-no-metrics": (c) =>
      addBulletsToSection(c, "Experience", [
        "- Led migration of legacy platform reducing latency by 40% across 12 teams in 6 months",
        "- Built ML pipeline serving 12M users daily, cutting processing time by 65%",
        "- Drove cross-functional initiative resulting in $2.4M annual cost savings",
      ]),
    "heur-fancy-format": (c) =>
      c.replace(/\b(table|column-layout|graphics|image|photo)\b/gi, ""),
    "heur-length-short": (c) =>
      addBulletsToSection(c, "Experience", [
        "**Company · Senior Role · 2020-Present**",
        "- Led [project] resulting in [40% improvement / $2M revenue / 12M users]",
        "- Built [system] used by [N customers], cutting [metric] by [%]",
        "- Mentored [N] engineers, shipping [N] critical features in [timeframe]",
        "- Architected [solution] processing [N requests/day], increasing [outcome] by [X%]",
      ]),
    "heur-length-long": (c) => c, // user trims manually
  };

  function ensureContact(c) {
    if (/@[\w.-]+\.\w+/.test(c)) return c;
    const lines = c.split("\n");
    const insertAt = lines[0].startsWith("# ") ? 1 : 0;
    lines.splice(insertAt, 0, "your.email@example.com · 555-123-4567 · linkedin.com/in/your-handle");
    return lines.join("\n");
  }

  function addSection(c, name, body, opts = {}) {
    if (new RegExp(`^##\\s+${name}\\b`, "mi").test(c)) return c;
    const block = `\n## ${name}\n${body}\n`;
    if (opts.atTop) {
      // After header lines (name + contact)
      const lines = c.split("\n");
      let insertAt = 0;
      while (insertAt < lines.length && (lines[insertAt].trim() === "" || lines[insertAt].startsWith("# ") || /@|·|\|/.test(lines[insertAt]))) {
        insertAt++;
        if (insertAt > 3) break;
      }
      lines.splice(insertAt, 0, block.trim(), "");
      return lines.join("\n");
    }
    return c.trimEnd() + "\n" + block;
  }

  function addBulletExample(c, sectionName, bullet) {
    const re = new RegExp(`(^##\\s+${sectionName}\\b[^\\n]*\\n)`, "im");
    if (re.test(c)) {
      return c.replace(re, `$1${bullet}\n`);
    }
    return addSection(c, sectionName, bullet);
  }

  function addBulletsToSection(c, sectionName, bullets) {
    const block = bullets.join("\n");
    const re = new RegExp(`(^##\\s+${sectionName}\\b[^\\n]*\\n)`, "im");
    if (re.test(c)) {
      return c.replace(re, `$1${block}\n`);
    }
    return addSection(c, sectionName, block);
  }

  function convertParagraphsToBullets(c) {
    // For any non-header, non-blank line ≥40 chars that doesn't already start with a bullet,
    // prepend "- "
    return c
      .split("\n")
      .map((ln) => {
        const trimmed = ln.trim();
        if (!trimmed) return ln;
        if (trimmed.startsWith("#")) return ln;
        if (trimmed.startsWith("-") || trimmed.startsWith("*") || trimmed.startsWith("•")) return ln;
        if (trimmed.length < 40) return ln;
        if (/^[A-Z][A-Z ]{2,}$/.test(trimmed)) return ln; // skip likely-header lines
        return `- ${trimmed}`;
      })
      .join("\n");
  }

  function applyAnalysisGap(c, suggestion) {
    // Insert a Skills/Experience bullet hinting at the gap
    const gapText = suggestion.title.replace(/^Address gap:\s*/i, "").trim();
    const isSkill = (gapText.length < 40) && !/\s(of|and|for|with|in)\s/i.test(gapText);
    if (isSkill) {
      // Append to Skills section
      const re = /(^##\s+Skills\b[^\n]*\n)([^\n]*)/im;
      if (re.test(c)) {
        return c.replace(re, (_, h, line) => `${h}${line.trim()}, ${gapText}\n`);
      }
      return addSection(c, "Skills", `Python · SQL · ${gapText}`);
    }
    // Else add a bullet to Experience hinting at adjacent experience
    return addBulletExample(c, "Experience",
      `- Demonstrated familiarity with ${gapText} via [project / context / training]`);
  }

  function applyAnalysisTweak(c, suggestion) {
    const tweakLabel = suggestion.title.replace(/^Suggested tweak:\s*/i, "");
    return addBulletExample(c, "Experience", `- ${tweakLabel}`);
  }

  function applyAnalysisStrength(c, suggestion) {
    const strength = suggestion.title.replace(/^Lead with this strength:\s*/i, "");
    return addBulletExample(c, "Summary", `Recognized for ${strength}.`);
  }

  const applyOne = (s, c) => {
    let next = ensureContact(c);
    const patcher = PATCH_TEMPLATES[s.id];
    if (patcher) {
      next = patcher(next);
    } else if (s.id.startsWith("analysis-gap-")) {
      next = applyAnalysisGap(next, s);
    } else if (s.id.startsWith("analysis-tweak-")) {
      next = applyAnalysisTweak(next, s);
    } else if (s.id.startsWith("analysis-strength-")) {
      next = applyAnalysisStrength(next, s);
    } else {
      next = addBulletExample(next, s.section.charAt(0).toUpperCase() + s.section.slice(1),
        `- ${s.title}`);
    }
    return next;
  };

  const applySuggestion = (s) => {
    if (applied.has(s.id)) return;
    // Snapshot the current content as the session baseline before the FIRST
    // apply of this session. Subsequent applies layer on top.
    if (sessionBaseline === null) setSessionBaseline(content);
    const next = applyOne(s, content);
    setContent(next);
    const nextApplied = new Set([...applied, s.id]);
    setApplied(nextApplied);
    setApplyOrder([...applyOrder, s.id]);
    persistApplied(next, nextApplied);
    setToast({ kind: "success", msg: "Tweak applied — watch the ATS score climb." });
    setTimeout(() => setToast(null), 1800);
  };

  const applyAllUnaddressed = (severities = ["critical", "warn"]) => {
    const pool = suggestions.filter((s) => severities.includes(s.severity) && !applied.has(s.id));
    if (pool.length === 0) return;
    // Snapshot once before the batch — keeps undo consistent.
    if (sessionBaseline === null) setSessionBaseline(content);
    let nextContent = content;
    const nextApplied = new Set(applied);
    const newOrder = [...applyOrder];
    for (const s of pool) {
      nextContent = applyOne(s, nextContent);
      nextApplied.add(s.id);
      newOrder.push(s.id);
    }
    setContent(nextContent);
    setApplied(nextApplied);
    setApplyOrder(newOrder);
    persistApplied(nextContent, nextApplied);
    setToast({ kind: "success", msg: `Applied ${pool.length} tweaks — recalculating score…` });
    setTimeout(() => setToast(null), 2200);
  };

  const undoSuggestion = (s) => {
    if (!applied.has(s.id)) return;
    const nextApplied = new Set(applied);
    nextApplied.delete(s.id);

    // If this id was applied in the current session, replay the remaining
    // session-applied tweaks on top of the baseline so the content (and the
    // ATS score that's computed from it) reverts correctly.
    const inSession = applyOrder.includes(s.id);
    if (inSession && sessionBaseline !== null) {
      const newOrder = applyOrder.filter((id) => id !== s.id);
      let recomputed = sessionBaseline;
      for (const id of newOrder) {
        const sg = suggestions.find((x) => x.id === id);
        if (sg) recomputed = applyOne(sg, recomputed);
      }
      setContent(recomputed);
      setApplyOrder(newOrder);
      setApplied(nextApplied);
      persistApplied(recomputed, nextApplied);
      setToast({ kind: "info", msg: "Tweak undone — ATS score will recalculate." });
      setTimeout(() => setToast(null), 1800);
      return;
    }

    // Prior-session apply (or no baseline yet) — clear the flag only. We
    // can't auto-revert the content edit; the user can still edit inline.
    setApplied(nextApplied);
    persistApplied(content, nextApplied);
    setToast({ kind: "info", msg: "Marked as not applied. Edit the resume above if you also want to remove the text." });
    setTimeout(() => setToast(null), 2400);
  };

  const persistApplied = async (newContent, newApplied) => {
    try {
      await api.post("/studio/save", {
        content_md: newContent,
        template_id: templateId,
        based_on_analysis_id: state?.latest_analysis_id,
        applied_ids: Array.from(newApplied),
      });
    } catch {
      // silent — autosave is best-effort
    }
  };

  const save = async () => {
    setSaveStatus("saving");
    try {
      await api.post("/studio/save", {
        content_md: content,
        template_id: templateId,
        based_on_analysis_id: state?.latest_analysis_id,
      });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(""), 2500);
    } catch (e) {
      setSaveStatus("");
      setToast({ kind: "error", msg: e.response?.data?.detail || "Save failed." });
    }
  };

  const saveContactInfo = async () => {
    setContactSaveStatus("saving");
    try {
      await api.patch("/studio/contact", {
        name: (contactInfo.name || "").trim(),
        email: (contactInfo.email || "").trim(),
        phone: (contactInfo.phone || "").trim(),
        linkedin_url: (contactInfo.linkedin_url || "").trim(),
        location: (contactInfo.location || "").trim(),
      });
      setContactSaveStatus("saved");
      setTimeout(() => setContactSaveStatus(""), 2200);
    } catch {
      setContactSaveStatus("");
      setToast({ kind: "error", msg: "Couldn't save your contact info. Try again." });
    }
  };

  const download = async (fmt) => {
    // Guard against stale/unknown template_id (e.g. older drafts with retired IDs).
    const validIds = new Set(templates.map((t) => t.id));
    const safeTemplateId = validIds.has(templateId) ? templateId : "classic_chronological";
    if (safeTemplateId !== templateId) {
      setTemplateId(safeTemplateId);
      setToast({
        kind: "info",
        msg: `That template is no longer available. Falling back to Classic Chronological.`,
      });
    }

    setDownloading(fmt);
    try {
      const resp = await api.post(
        "/studio/render",
        {
          content_md: content,
          template_id: safeTemplateId,
          fmt,
          // Ship the current contact-info form values so the header on the
          // downloaded resume matches what the user sees in the panel above.
          contact: {
            name: (contactInfo.name || "").trim(),
            email: (contactInfo.email || "").trim(),
            phone: (contactInfo.phone || "").trim(),
            linkedin_url: (contactInfo.linkedin_url || "").trim(),
            location: (contactInfo.location || "").trim(),
          },
        },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resume_${safeTemplateId}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setToast({ kind: "success", msg: `Downloaded resume as ${fmt.toUpperCase()}` });
      setTimeout(() => setToast(null), 2500);
      if (state?.analysis_context?.job_url) setApplyNudge(true);
    } catch (e) {
      // With responseType:'blob' axios returns the JSON error body as a Blob —
      // we have to read it as text to surface the real `detail` to the user.
      let detail = "";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try {
          const txt = await blob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) {
            detail = typeof parsed.detail === "string"
              ? parsed.detail
              : JSON.stringify(parsed.detail);
          }
        } catch { /* not JSON, ignore */ }
      }
      const status = e?.response?.status;
      const niceStatus = status === 404
        ? "couldn't reach the export service"
        : status === 400
          ? "the template wasn't recognized"
          : status === 402
            ? "credit balance is too low"
            : `request failed (${status || "network error"})`;
      setToast({
        kind: "error",
        msg: detail
          ? `Download failed — ${detail}`
          : `Download failed — ${niceStatus}. Try refreshing or pick a different template.`,
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setDownloading(null);
    }
  };

  // ---- Job connection (apply / re-check fit / back to report) ----
  const jobCtx = state?.analysis_context || null;

  useEffect(() => {
    const aid = jobCtx?.analysis_id;
    if (!aid || !jobCtx?.job_url) return;
    api.get(`/outcomes/by-analysis/${aid}`)
      .then((r) => { if (r.data?.id) setAppliedToJob(true); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobCtx?.analysis_id]);

  const handleApplyToJob = async () => {
    if (!jobCtx?.job_url) return;
    window.open(jobCtx.job_url, "_blank", "noopener");
    setApplyNudge(false);
    if (appliedToJob) return;
    try {
      await api.post("/outcomes", {
        analysis_id: jobCtx.analysis_id,
        status: "applied",
        notes: "Applied via Resmo Studio → employer site",
      });
      setAppliedToJob(true);
      window.dispatchEvent(new CustomEvent("resmo:spark", { detail: { kind: "apply" } }));
      window.dispatchEvent(new CustomEvent("resmo:outcome:refresh", { detail: { analysisId: jobCtx.analysis_id } }));
    } catch { /* non-fatal — the posting still opened */ }
  };

  const handleRecheckFit = async () => {
    if (!jobCtx?.analysis_id || recheckLoading) return;
    setRecheckError("");
    setRecheckLoading(true);
    try {
      // Save the draft first so the backend re-checks exactly what's on screen.
      await api.post("/studio/save", {
        content_md: content,
        template_id: templateId,
        based_on_analysis_id: state?.latest_analysis_id,
        applied_ids: Array.from(applied),
      });
      const { data } = await api.post(`/analyses/${jobCtx.analysis_id}/recheck-fit`);
      setToast({ kind: "info", msg: "Resmo is re-reading your resume against the job — hang tight…" });
      const until = Date.now() + 1000 * 60 * 3;
      while (Date.now() < until) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: st } = await api.get(`/jobs/${data.job_id}`);
          if (st.status === "complete" && st.analysis_id) {
            setToast(null);
            navigate(`/dashboard/${st.analysis_id}`);
            return;
          }
          if (st.status === "error") {
            setRecheckError(formatApiErrorDetail(st.error) || "Re-check failed. Try again.");
            setToast(null);
            setRecheckLoading(false);
            return;
          }
        } catch { /* keep polling */ }
      }
      setRecheckError("Resmo took too long. Try again.");
      setToast(null);
      setRecheckLoading(false);
    } catch (e) {
      setRecheckError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setToast(null);
      setRecheckLoading(false);
    }
  };

  const suggestions = useMemo(() => state?.suggestions || [], [state]);
  const templates = useMemo(() => state?.templates || [], [state]);
  const counts = useMemo(() => {
    const out = { critical: 0, warn: 0, info: 0 };
    for (const s of suggestions) out[s.severity] = (out[s.severity] || 0) + 1;
    return out;
  }, [suggestions]);

  const atsReady = !ats || (ats?.score ?? 0) >= 60;
  const gateMsg = atsReady ? "" : `Push above 60 first — current ${ats.score}.`;

  if (loading) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <Loader className="mx-auto animate-spin rm-text-orange" size={32} />
          <div className="mt-4 font-mono-rm uppercase text-xs tracking-widest"
               style={{ color: "#475569" }}>
            Loading the Studio…
          </div>
        </div>
      </div>
    );
  }

  if (!state?.has_resume) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="rm-card-static p-6"
               style={{ background: "white", borderColor: "#0F1B2D" }}>
            <Wand2 size={32} className="rm-text-orange mx-auto mb-3" />
            <div className="font-display uppercase text-2xl mb-2">Upload a resume first</div>
            <p className="text-sm mb-4" style={{ color: "#475569" }}>
              The Resume Studio works on your base resume. Drop one in and come back.
            </p>
            <button onClick={() => navigate("/feed-resume")}
                    className="rm-btn rm-btn-primary text-sm" data-testid="studio-upload-cta">
              Upload resume
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper" data-testid="studio-page">
      <Navbar />

      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-[90%]"
             data-testid="studio-toast"
             style={{
               background: toast.kind === "success" ? "#1A7F5C" : toast.kind === "error" ? "#DC2626" : "#0F1B2D",
               color: "white", border: "3px solid #0F1B2D",
               boxShadow: "4px 4px 0 0 #FF6A00", padding: "10px 14px",
             }}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            {toast.kind === "success" && <Check size={14} />}
            {toast.msg}
          </div>
        </div>
      )}

      {/* Post-download nudge — the natural moment to apply is right after the
          polished resume lands in their downloads folder. */}
      {applyNudge && jobCtx?.job_url && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm w-[92%] sm:w-auto"
             data-testid="studio-apply-nudge"
             style={{
               background: "#0F1B2D", color: "white",
               border: "3px solid #0F1B2D", boxShadow: "4px 4px 0 0 #FF6A00",
               padding: "14px 38px 14px 16px", borderRadius: 6, position: "fixed",
             }}>
          <button onClick={() => setApplyNudge(false)}
                  className="absolute top-2 right-2 opacity-70 hover:opacity-100"
                  style={{ color: "white" }}
                  aria-label="Dismiss"
                  data-testid="studio-apply-nudge-dismiss">
            <X size={14} />
          </button>
          <div className="font-display text-sm mb-1" style={{ color: "#FFC145" }}>
            Your tailored resume is ready.
          </div>
          <p className="text-xs mb-3" style={{ color: "#CBD5E1", maxWidth: 300 }}>
            Strike while it's hot — apply to{" "}
            <b style={{ color: "white" }}>{jobCtx.job_title || "the role"}</b>
            {jobCtx.company_name ? ` at ${jobCtx.company_name}` : ""} with the resume you just polished.
          </p>
          <button onClick={handleApplyToJob}
                  className="rm-btn text-xs w-full justify-center"
                  style={{ background: "#B45309", color: "white" }}
                  data-testid="studio-apply-nudge-btn">
            <Send size={13} /> Apply on employer site
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="mb-5 md:mb-6 flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-2">
              · Resume Studio
            </div>
            <h1 className="font-display leading-tight mb-3"
                style={{ fontSize: "clamp(1.5rem, 2.6vw, 2rem)", color: "#0F1B2D", letterSpacing: "-0.015em", fontWeight: 700 }}>
              Polish your resume in three steps.
            </h1>

            {/* Job connection bar — keeps the thread to the job the user came
                from: re-check fit with the edited resume, apply on the
                employer site, or jump back to the fit report. */}
            {jobCtx && (
              <div
                className="mb-3 inline-flex flex-wrap items-center gap-2 px-3 py-2"
                style={{
                  background: "#FFF7E6",
                  border: "2px solid #0F1B2D",
                  boxShadow: "2px 2px 0 0 #FF6A00",
                  borderRadius: 4,
                }}
                data-testid="studio-job-bar"
              >
                <Sparkles size={12} className="rm-text-orange" />
                <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                      style={{ color: "#475569" }}>
                  Tailoring for
                </span>
                <span className="font-display text-sm" style={{ color: "#0F1B2D" }}>
                  {jobCtx.job_title || "your latest role"}
                  {jobCtx.company_name && (
                    <span style={{ color: "#475569", fontWeight: 400 }}>
                      {" @ "}{jobCtx.company_name}
                    </span>
                  )}
                </span>
                {jobCtx.fit_score && (
                  <span className="font-mono-rm text-[10px] px-1.5 py-0.5 rounded"
                        style={{
                          background: jobCtx.fit_score >= 75 ? "#10B981" :
                                       jobCtx.fit_score >= 60 ? "#FFC145" : "#DC2626",
                          color: "white",
                        }}>
                    fit {jobCtx.fit_score}/100
                  </span>
                )}
                <span className="hidden sm:inline-block mx-1"
                      style={{ width: 1, height: 18, background: "#0F1B2D", opacity: 0.25 }} />
                {jobCtx.analysis_id && (
                  <button
                    onClick={handleRecheckFit}
                    disabled={recheckLoading}
                    className="rm-btn text-[11px] px-2.5 py-1 disabled:opacity-60"
                    style={{ background: "#0F1B2D", color: "white" }}
                    title="Re-run the fit analysis against this job with your edited resume — see how much your score improved"
                    data-testid="studio-recheck-fit-btn"
                  >
                    {recheckLoading ? <Loader size={12} className="animate-spin" /> : <Gauge size={12} />}
                    {recheckLoading ? "Re-checking…" : "Re-check my fit"}
                  </button>
                )}
                {jobCtx.job_url && (
                  <button
                    onClick={handleApplyToJob}
                    className="rm-btn text-[11px] px-2.5 py-1"
                    style={{ background: appliedToJob ? "#0E5A4A" : "#B45309", color: "white" }}
                    title="Opens the employer's application page in a new tab and logs 'Applied' on this analysis"
                    data-testid="studio-apply-btn"
                  >
                    <Send size={12} /> {appliedToJob ? "Applied ✓ · Reopen posting" : "Apply on employer site"}
                  </button>
                )}
                {jobCtx.analysis_id && (
                  <button
                    onClick={() => navigate(`/dashboard/${jobCtx.analysis_id}`)}
                    className="rm-btn text-[11px] px-2.5 py-1"
                    style={{ background: "transparent", color: "#0F1B2D" }}
                    title="Back to this job's fit report"
                    data-testid="studio-back-to-report-btn"
                  >
                    <ArrowLeft size={12} /> Fit report
                  </button>
                )}
              </div>
            )}
            {recheckError && (
              <div className="mb-3 text-xs font-semibold" style={{ color: "#DC2626" }}
                   data-testid="studio-recheck-error">
                {recheckError}
              </div>
            )}

            <ol className="space-y-1.5 text-[13px]" style={{ color: "#334155" }} data-testid="studio-steps">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 inline-flex items-center justify-center font-mono-rm font-bold"
                      style={{ width: 22, height: 22, borderRadius: 6, background: "#FFF4E0", color: "#B91C1C", fontSize: 11 }}>1</span>
                <span><b style={{ color: "#0F1B2D" }}>Apply Resmo's tweaks.</b> Review the suggestions on the left — apply them one-by-one or in bulk.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 inline-flex items-center justify-center font-mono-rm font-bold"
                      style={{ width: 22, height: 22, borderRadius: 6, background: "#FFF4E0", color: "#B91C1C", fontSize: 11 }}>2</span>
                <span><b style={{ color: "#0F1B2D" }}>Pick an ATS-friendly template.</b> Tap any template on the right to preview the format before choosing.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 inline-flex items-center justify-center font-mono-rm font-bold"
                      style={{ width: 22, height: 22, borderRadius: 6, background: "#FFF4E0", color: "#B91C1C", fontSize: 11 }}>3</span>
                <span><b style={{ color: "#0F1B2D" }}>Download.</b> Once your ATS score hits 60+, export as DOCX or PDF.</span>
              </li>
            </ol>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => window.dispatchEvent(new CustomEvent("resmo:publish-panel:open"))}
                    className="rm-btn text-xs"
                    style={{ background: "#0E5A4A", color: "white" }}
                    title="Post your resume to the Candidates Store so employers can find you"
                    data-testid="studio-post-to-store-btn">
              <Store size={14} /> Post to Store
            </button>
            <button onClick={save}
                    disabled={saveStatus === "saving"}
                    className="rm-btn rm-btn-secondary text-xs"
                    data-testid="studio-save-btn">
              <Save size={14} />
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved ✓" : "Save draft"}
            </button>
            <button onClick={() => download("docx")}
                    disabled={downloading === "docx" || !atsReady}
                    title={!atsReady ? `Your resume isn't ATS-ready yet — ${gateMsg}` : "Download as DOCX"}
                    className="rm-btn rm-btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="studio-download-docx">
              <Download size={14} />
              {downloading === "docx" ? "Building…" : "DOCX"}
            </button>
            <button onClick={() => download("pdf")}
                    disabled={downloading === "pdf" || !atsReady}
                    title={!atsReady ? `Your resume isn't ATS-ready yet — ${gateMsg}` : "Download as PDF"}
                    className="rm-btn rm-btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="studio-download-pdf"
                    style={{ background: "#1E3A5F", color: "#FFC145" }}>
              <Download size={14} />
              {downloading === "pdf" ? "Building…" : "PDF"}
            </button>
          </div>
        </div>

        {focusParam && !focusDismissed && (
          <div className="rm-card-static p-4 mb-5 flex items-start gap-3"
               style={{ background: "#FFF2EC", borderColor: "#FF6A00" }}
               data-testid="studio-focus-banner">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: "#FF6A00", color: "white", border: "2px solid #0F1B2D" }}>
              <Zap size={17} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest font-bold" style={{ color: "#FF6A00" }}>
                · Focus mission — your biggest lever
              </div>
              <div className="font-bold text-sm leading-snug" style={{ color: "#0F1B2D" }} data-testid="studio-focus-text">
                {focusParam}
              </div>
              <div className="text-xs mt-1" style={{ color: "#64748B" }}>
                Work evidence of this into your resume — Resmo's Tweaks on the left include gap fixes, and the ATS score updates live as you edit.
              </div>
            </div>
            <button type="button" onClick={() => setFocusDismissed(true)}
                    className="flex-shrink-0 p-1 hover:opacity-70" title="Dismiss"
                    data-testid="studio-focus-dismiss">
              <X size={15} style={{ color: "#64748B" }} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

          {/* LEFT — Suggestions grouped by severity */}
          <aside className="lg:col-span-3" data-testid="studio-suggestions">
            <div className="rm-card-static p-3 mb-3"
                 style={{ background: "#0F1B2D", borderColor: "#0F1B2D" }}>
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
                   style={{ color: "#FFC145", fontWeight: 700 }}>
                Resmo's Tweaks
              </div>
              <div className="flex gap-2 flex-wrap mb-2">
                <SevPill color="#DC2626" label="critical" n={counts.critical || 0} />
                <SevPill color="#E8AF31" label="warn"     n={counts.warn || 0}     />
                <SevPill color="#20C1A7" label="info"     n={counts.info || 0}     />
              </div>
              {suggestions.some((s) => !applied.has(s.id) && (s.severity === "critical" || s.severity === "warn")) && (
                <button
                  type="button"
                  onClick={() => applyAllUnaddressed(["critical", "warn"])}
                  className="w-full rm-btn rm-btn-primary text-[10px] py-1.5 justify-center mb-1"
                  data-testid="apply-all-btn"
                  style={{ background: "#FF6A00", borderColor: "#FFC145", color: "#0F1B2D" }}
                >
                  <Wand2 size={11} /> Apply all critical + warn
                </button>
              )}
              <div className="text-[10px]" style={{ color: "#94A3B8" }}>
                Tap any tweak to expand · critical first.
              </div>
            </div>

            {suggestions.length === 0 ? (
              <div className="rm-card-static p-4 text-center" style={{ background: "white" }}>
                <Check size={24} className="mx-auto mb-1" style={{ color: "#1A7F5C" }} />
                <div className="text-sm font-semibold">Looking clean!</div>
                <div className="text-xs" style={{ color: "#475569" }}>
                  No structural ATS issues found.
                </div>
              </div>
            ) : (
              <>
                <SeverityGroup
                  title="Critical · fix first"
                  color="#DC2626"
                  items={suggestions.filter((s) => s.severity === "critical")}
                  applied={applied}
                  onApply={applySuggestion}
                  onUndo={undoSuggestion}
                  onFocus={focusSection}
                  defaultOpen={false}
                />
                <SeverityGroup
                  title="Should fix"
                  color="#E8AF31"
                  items={suggestions.filter((s) => s.severity === "warn")}
                  applied={applied}
                  onApply={applySuggestion}
                  onUndo={undoSuggestion}
                  onFocus={focusSection}
                  defaultOpen={false}
                />
                <SeverityGroup
                  title="Nice-to-have polish"
                  color="#20C1A7"
                  items={suggestions.filter((s) => s.severity === "info")}
                  applied={applied}
                  onApply={applySuggestion}
                  onUndo={undoSuggestion}
                  onFocus={focusSection}
                  defaultOpen={false}
                />
              </>
            )}
          </aside>

          {/* CENTER — Editor */}
          <main className="lg:col-span-6">
            <AtsScoreBadge ats={ats} loading={atsLoading} />
            {!atsReady && (
              <div
                className="rm-card-static px-3 py-2 mb-3 flex items-center gap-2"
                style={{
                  background: "#FFF8E7",
                  borderColor: "#DC2626",
                  boxShadow: "3px 3px 0 0 #DC2626",
                }}
                data-testid="ats-gate-warning"
              >
                <AlertTriangle size={16} style={{ color: "#DC2626" }} />
                <div className="text-xs leading-snug" style={{ color: "#0F1B2D" }}>
                  <span className="font-semibold">Downloads locked.</span>{" "}
                  Your resume needs to hit <b className="rm-text-orange">ATS 60+</b> to download.
                  Apply Resmo's suggestions on the left and watch the score climb.
                </div>
              </div>
            )}
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                 style={{ color: "#475569" }}>
              · Resume content (edit anything)
            </div>
            <ContactInfoPanel
              open={contactPanelOpen}
              onToggle={() => setContactPanelOpen((v) => !v)}
              contact={contactInfo}
              onChange={setContactInfo}
              onSave={saveContactInfo}
              saveStatus={contactSaveStatus}
              loaded={contactInfoLoaded}
            />
            <textarea
              ref={editorRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full text-[13px] p-5 focus:outline-none focus:ring-2"
              style={{
                minHeight: 580,
                background: "#F8FAFC",
                border: focusedSection ? "2px solid #FF6A00" : "1px solid #E2E8F0",
                borderRadius: 8,
                color: "#334155",
                fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
                fontWeight: 400,
                lineHeight: 1.7,
                letterSpacing: "0.005em",
                resize: "vertical",
                boxShadow: focusedSection
                  ? "0 0 0 4px rgba(255,106,0,0.25), 0 1px 2px rgba(15,27,45,0.04)"
                  : "0 1px 2px rgba(15,27,45,0.04), 0 1px 3px rgba(15,27,45,0.06)",
                transition: "border-color 0.18s ease, box-shadow 0.18s ease",
              }}
              spellCheck
              data-testid="studio-editor"
              placeholder="# Your Name
your.email@example.com · 555-123-4567 · linkedin.com/in/you

## Summary
Two- to three-sentence elevator pitch leading with your most recent role.

## Experience
**Company · Role · 2020-present**
- Led X to achieve Y, resulting in Z (with metric).
- ..."
            />

            {/* Resume Honesty Check — collapsible tab, only renders when this
                Studio draft is tied to an analysis with an integrity audit. */}
            {state?.latest_analysis_id && (
              <StudioIntegritySection analysisId={state.latest_analysis_id} />
            )}
          </main>

          {/* RIGHT — Templates (collapsible) */}
          <aside className="lg:col-span-3" data-testid="studio-templates">
            <TemplatePicker
              templates={templates}
              templateId={templateId}
              onPick={setTemplateId}
            />
            <ShareTemplateCard />
            {/* Post to the Candidates Store — always available */}
            <StudioPublishPanel analysisId={state?.latest_analysis_id || null} />
            {/* Live openings scored against the resume being polished */}
            <JobMatchesPanel className="mt-4" limit={3} />
          </aside>
        </div>
      </div>
    </div>
  );
}


/* ============================================================ */
/*  ATS Readiness Score — live badge above the editor           */
/* ============================================================ */
function AtsScoreBadge({ ats, loading }) {
  if (!ats && !loading) return null;
  const score = ats?.score ?? 0;
  const band = ats?.band || "fair";
  const color =
    band === "excellent" ? "#1A7F5C" :
    band === "good" ? "#20C1A7" :
    band === "fair" ? "#E8AF31" : "#DC2626";
  const ringPct = Math.max(0, Math.min(100, score));

  return (
    <div
      className="rm-card-static p-3 mb-3"
      style={{
        background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 100%)",
        borderColor: "#0F1B2D",
        boxShadow: `4px 4px 0 0 ${color}`,
      }}
      data-testid="ats-score-badge"
    >
      <div className="flex items-center gap-3 flex-wrap">
        {/* Ring */}
        <div
          className="flex-shrink-0 flex items-center justify-center font-display"
          style={{
            width: 64, height: 64, borderRadius: "50%",
            background: `conic-gradient(${color} ${ringPct}%, rgba(255,255,255,0.08) ${ringPct}% 100%)`,
            border: "2px solid " + color,
          }}
        >
          <div
            className="flex items-center justify-center font-display"
            style={{
              width: 48, height: 48, borderRadius: "50%",
              background: "#0F1B2D",
              color: color,
              fontSize: "1.05rem",
            }}
            data-testid="ats-score-value"
          >
            {loading ? "…" : score}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Gauge size={12} style={{ color }} />
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color, fontWeight: 700 }}>
              ATS Readiness · live
            </div>
          </div>
          <div className="text-sm font-semibold leading-snug" style={{ color: "white" }}>
            {loading ? "Recalculating…" : (ats?.verdict || "—")}
          </div>
          {ats && (
            <div className="text-[10px] mt-0.5" style={{ color: "#94A3B8" }}>
              {ats.word_count} words · {ats.bullet_count} bullets
            </div>
          )}
        </div>
      </div>

      {/* Dimension breakdown */}
      {ats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mt-3" data-testid="ats-dimensions">
          {Object.entries(ats.dimensions).map(([k, d]) => {
            const dimColor = d.score >= 80 ? "#20C1A7" : d.score >= 55 ? "#E8AF31" : "#DC2626";
            return (
              <div key={k}
                   className="px-2 py-1.5"
                   style={{
                     background: "rgba(255,255,255,0.04)",
                     border: `1px solid ${dimColor}`,
                     borderRadius: 4,
                   }}
                   data-testid={`ats-dim-${k}`}>
                <div className="text-[9px] uppercase font-mono-rm tracking-widest leading-none"
                     style={{ color: dimColor, fontWeight: 700 }}>
                  {d.score}
                </div>
                <div className="text-[10px] leading-snug mt-0.5" style={{ color: "#CBD5E1" }}>
                  {d.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  Severity pill — small count chip in the header              */
/* ============================================================ */
function SevPill({ color, label, n }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-rm uppercase tracking-widest"
      style={{
        background: "rgba(255,255,255,0.06)",
        color: color,
        border: `1.5px solid ${color}`,
        borderRadius: 4,
        fontWeight: 700,
      }}
    >
      <b style={{ color: "white", fontSize: "12px" }}>{n}</b>
      {label}
    </span>
  );
}

/* ============================================================ */
/*  SeverityGroup — collapsible group of suggestion cards       */
/* ============================================================ */
function SeverityGroup({ title, color, items, applied, onApply, onUndo, onFocus, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (items.length === 0) return null;
  const remaining = items.filter((s) => !applied.has(s.id)).length;
  return (
    <div className="mb-3" data-testid={`sev-group-${color}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 transition-colors"
        style={{
          background: "#FFFEF7",
          border: `2px solid ${color}`,
          borderRadius: 6,
          boxShadow: `3px 3px 0 0 ${color}`,
          cursor: "pointer",
        }}
        data-testid={`sev-group-toggle-${color}`}
      >
        <div className="flex-1 text-left min-w-0">
          <div className="text-xs font-semibold leading-tight" style={{ color: "#0F1B2D" }}>
            {title}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: "#475569" }}>
            {remaining} of {items.length} remaining
          </div>
        </div>
        <div style={{ color }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      {open && (
        <div className="space-y-2 mt-2">
          {items.map((s) => (
            <SuggestionCard
              key={s.id}
              s={s}
              color={color}
              isApplied={applied.has(s.id)}
              onApply={onApply}
              onUndo={onUndo}
              onFocus={onFocus}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  SuggestionCard — compact, expandable on click               */
/* ============================================================ */
function SuggestionCard({ s, color, isApplied, onApply, onUndo, onFocus }) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    // When opening a card, highlight the section in the resume editor so
    // the user immediately sees WHERE on the resume this tweak applies.
    // We do this OUTSIDE the setState updater so React doesn't see it as a
    // 'setState during render' from a sibling component.
    if (next && onFocus && s.section) onFocus(s.section);
  };

  return (
    <div
      className="rm-card-static overflow-hidden"
      style={{
        background: isApplied ? "#F0FDF4" : "white",
        borderColor: "#0F1B2D",
        boxShadow: `2px 2px 0 0 ${color}`,
      }}
      data-testid={`suggestion-${s.id}`}
    >
      <button
        type="button"
        onClick={toggleExpanded}
        className="w-full text-left px-3 py-2 flex items-start gap-2"
        style={{ cursor: "pointer" }}
        data-testid={`suggestion-${s.id}-toggle`}
      >
        {isApplied ? (
          <Check size={13} style={{ color: "#1A7F5C", marginTop: 2, flexShrink: 0 }} />
        ) : (
          <span
            className="flex-shrink-0 mt-1.5"
            style={{
              width: 8, height: 8, borderRadius: "50%",
              background: color, display: "inline-block",
            }}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide" style={{ color, fontWeight: 700 }}>
            {s.section}
          </div>
          <div className="text-[13px] font-semibold leading-snug"
               style={{
                 color: isApplied ? "#475569" : "#0F1B2D",
                 textDecoration: isApplied ? "line-through" : "none",
               }}>
            {s.title}
          </div>
        </div>
        <ChevronDown
          size={14}
          style={{
            color: "#94A3B8",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
            marginTop: 4,
          }}
        />
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-0">
          <p className="text-[12px] leading-relaxed mb-2"
             style={{ color: "#475569" }}
             data-testid={`suggestion-${s.id}-detail`}>
            {s.detail}
          </p>
          {isApplied ? (
            <button
              onClick={(e) => { e.stopPropagation(); onUndo(s); }}
              className="rm-btn rm-btn-ghost text-[10px] w-full justify-center py-1"
              data-testid={`suggestion-${s.id}-undo`}
            >
              <Check size={11} /> Applied · undo
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onApply(s); }}
              className="rm-btn rm-btn-primary text-[10px] w-full justify-center py-1"
              data-testid={`suggestion-${s.id}-apply`}
            >
              <Plus size={11} /> Apply this tweak
            </button>
          )}
        </div>
      )}
    </div>
  );
}


/* ============================================================ */
/*  TemplatePicker — single "Resume Templates" master collapsible */
/*  with template rows styled like ATS suggestion cards.          */
/*  Each row expands to show tagline, best-for, and a sample      */
/*  ASCII preview of the format before selecting.                 */
/* ============================================================ */
function TemplatePicker({ templates, templateId, onPick }) {
  const [groupOpen, setGroupOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  if (!templates.length) return null;

  const selected = templates.find((t) => t.id === templateId);

  return (
    <div data-testid="template-picker">
      {/* Master "Resume Templates" header — same style as severity group */}
      <button
        type="button"
        onClick={() => setGroupOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 mb-2 transition-colors"
        style={{
          background: "#FFFEF7",
          border: "2px solid #FF6A00",
          borderRadius: 6,
          boxShadow: "3px 3px 0 0 #FF6A00",
          cursor: "pointer",
        }}
        data-testid="template-group-toggle"
        aria-expanded={groupOpen}
      >
        <div className="flex-1 text-left min-w-0">
          <div className="text-xs font-semibold leading-tight" style={{ color: "#0F1B2D" }}>
            Resume Templates
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: "#475569" }}>
            {templates.length} ATS-friendly options · using <b>{selected?.label || "—"}</b>
          </div>
        </div>
        <div style={{ color: "#FF6A00" }}>
          {groupOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Template rows — only visible when group is expanded */}
      {groupOpen && (
        <div className="space-y-2">
          {templates.map((t) => {
            const isSelected = templateId === t.id;
            const isOpen = openId === t.id;
            const accent = isSelected ? "#FF6A00" : "#94A3B8";
            return (
              <div
                key={t.id}
                className="rm-card-static overflow-hidden"
                style={{
                  background: isSelected ? "#FFF4E0" : "white",
                  borderColor: "#0F1B2D",
                  boxShadow: `2px 2px 0 0 ${accent}`,
                }}
                data-testid={`template-${t.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : t.id)}
                  className="w-full text-left px-3 py-2 flex items-start gap-2"
                  style={{ cursor: "pointer" }}
                  data-testid={`template-${t.id}-toggle`}
                >
                  {isSelected ? (
                    <Check size={13} style={{ color: "#1A7F5C", marginTop: 2, flexShrink: 0 }} />
                  ) : (
                    <span
                      className="flex-shrink-0 mt-1.5"
                      style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: accent, display: "inline-block",
                      }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide" style={{ color: accent, fontWeight: 700 }}>
                      ATS {t.ats_score}
                    </div>
                    <div className="text-[13px] font-semibold leading-snug" style={{ color: "#0F1B2D" }}>
                      {t.label}
                    </div>
                  </div>
                  <ChevronDown
                    size={14}
                    style={{
                      color: "#94A3B8",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                      flexShrink: 0,
                      marginTop: 4,
                    }}
                  />
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pt-0" data-testid={`template-${t.id}-detail`}>
                    <p className="text-[12px] leading-relaxed mb-2"
                       style={{ color: "#475569" }}>
                      {t.tagline}
                    </p>
                    <div className="text-[11px] mb-2" style={{ color: "#0F1B2D" }}>
                      <b>Best for:</b> {t.best_for}
                    </div>

                    {/* Sample preview — ASCII sketch of the layout */}
                    {t.sample_preview && (
                      <div className="mb-2">
                        <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-1"
                             style={{ color: "#64748B", fontWeight: 700 }}>
                          Sample layout
                        </div>
                        <pre
                          className="text-[10px] leading-snug overflow-hidden whitespace-pre-wrap m-0 p-2.5"
                          style={{
                            background: "#F8FAFC",
                            border: "1px solid #E2E8F0",
                            borderRadius: 4,
                            color: "#475569",
                            fontFamily: "'Poppins', system-ui, sans-serif",
                            maxHeight: 220,
                          }}
                          data-testid={`template-${t.id}-sample`}
                        >
                          {t.sample_preview}
                        </pre>
                      </div>
                    )}

                    {isSelected ? (
                      <div className="font-mono-rm uppercase text-[10px] tracking-widest text-center py-1"
                           style={{ color: "#1A7F5C", fontWeight: 700 }}>
                        ✓ Selected · downloads use this
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); onPick(t.id); }}
                        className="rm-btn rm-btn-primary text-[10px] w-full justify-center py-1"
                        data-testid={`template-${t.id}-select`}
                      >
                        <Check size={11} /> Use this template
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



/* ============================================================ */
/*  ShareTemplateCard — community CTA in the right rail         */
/* ============================================================ */
function ShareTemplateCard() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!file) { setError("Pick a file first."); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Max 5 MB."); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", note);
      await api.post("/studio/share-template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      setFile(null);
      setNote("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't share that — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rm-card-static p-4 mt-4"
      style={{
        background: "linear-gradient(135deg,#FFFBEB 0%,#FFF2EC 100%)",
        borderColor: "#0F1B2D",
      }}
      data-testid="share-template-card"
    >
      <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
           style={{ color: "#B45309", fontWeight: 700 }}>· Pay it forward</div>
      <div className="font-display text-base uppercase leading-tight">Share a winning template.</div>
      <p className="text-sm mt-2" style={{ color: "#475569" }}>
        Got a resume layout that <em>actually</em> landed you interviews? Send it
        to Resmo and we'll consider adding it to the gallery (credit to you).
      </p>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="rm-btn rm-btn-secondary text-xs px-3 py-2 mt-3"
          data-testid="share-template-open-btn"
        >
          <Plus size={13} /> Submit a template
        </button>
      ) : done ? (
        <div className="mt-3 p-3 rounded" style={{ background: "#ECFDF5", border: "2px solid #10B981", color: "#065F46" }}
             data-testid="share-template-done">
          <div className="font-display text-sm uppercase">Got it. Thank you!</div>
          <p className="text-xs mt-1">Resmo will email you if your template makes it into the gallery.</p>
          <button onClick={() => { setDone(false); setOpen(false); }}
                  className="rm-btn rm-btn-ghost text-[10px] px-2 py-1 mt-2"
                  data-testid="share-template-close-btn">Close</button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <label className="block rm-btn rm-btn-ghost text-xs px-3 py-2 cursor-pointer w-full justify-start"
                 data-testid="share-template-file-label">
            <Plus size={13} />
            {file ? file.name : "Choose a PDF / DOCX"}
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="share-template-file-input"
            />
          </label>
          <textarea
            className="rm-input w-full"
            rows={2}
            placeholder="What worked about it? (role, # interviews landed, etc.)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="share-template-note"
          />
          {error && (
            <div className="text-xs font-mono-rm" style={{ color: "#991B1B" }} data-testid="share-template-error">{error}</div>
          )}
          <div className="flex gap-2">
            <button onClick={submit} disabled={sending || !file}
                    className="rm-btn rm-btn-primary text-xs px-3 py-2"
                    data-testid="share-template-submit-btn">
              {sending ? <Loader size={13} className="animate-spin" /> : <ArrowRight size={13} />}
              {sending ? "Sending…" : "Send to Resmo"}
            </button>
            <button onClick={() => { setOpen(false); setFile(null); setNote(""); setError(""); }}
                    className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                    data-testid="share-template-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}



/* ============================================================ */
/*  StudioIntegritySection — collapsible honesty audit in Studio */
/* ============================================================ */
function StudioIntegritySection({ analysisId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 rm-card-static overflow-hidden"
         style={{ background: "white", borderColor: "#B91C1C" }}
         data-testid="studio-integrity-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        style={{ background: open ? "#FEE2E2" : "#FEF2F2" }}
        data-testid="studio-integrity-toggle"
      >
        <div className="flex items-center gap-2.5">
          <ShieldCheck size={18} style={{ color: "#B91C1C" }} />
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: "#B91C1C", fontWeight: 700 }}>
              · Resume honesty check
            </div>
            <div className="font-display uppercase leading-tight text-sm"
                 style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
              Review flagged claims before you download
            </div>
          </div>
        </div>
        {open
          ? <ChevronUp size={16} style={{ color: "#B91C1C" }} />
          : <ChevronDown size={16} style={{ color: "#B91C1C" }} />}
      </button>
      {open && (
        <div className="p-3 md:p-4" style={{ borderTop: "2px dashed #B91C1C" }}>
          <IntegrityPanel analysisId={analysisId} />
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  ContactInfoPanel — editable contact header for downloads    */
/* ============================================================ */
function ContactInfoPanel({ open, onToggle, contact, onChange, onSave, saveStatus, loaded }) {
  const preview = [contact.email, contact.phone, contact.location, contact.linkedin_url]
    .filter((s) => s && s.trim())
    .join(" · ");
  const set = (k) => (e) => onChange({ ...contact, [k]: e.target.value });
  return (
    <div className="rm-card-static overflow-hidden mb-3"
         style={{ background: "white", borderColor: "#0F1B2D" }}
         data-testid="studio-contact-panel">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="studio-contact-body"
        className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left"
        style={{ background: open ? "#FFF8E7" : "#F8FAFC" }}
        data-testid="studio-contact-toggle"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <User size={16} style={{ color: "#0F1B2D" }} />
          <div className="min-w-0">
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: "#475569", fontWeight: 700 }}>
              · Contact header on your resume
            </div>
            <div className="text-[12px] mt-0.5 truncate" style={{ color: "#0F1B2D", fontWeight: 600 }}
                 data-testid="studio-contact-preview">
              {loaded
                ? (contact.name
                    ? `${contact.name}${preview ? " · " + preview : ""}`
                    : "Add your name and details so downloads carry your contact info.")
                : "Loading…"}
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={16} style={{ color: "#0F1B2D" }} />
              : <ChevronDown size={16} style={{ color: "#0F1B2D" }} />}
      </button>
      {open && (
        <div id="studio-contact-body" className="p-3 md:p-4"
             style={{ borderTop: "1.5px dashed #0F1B2D" }}
             data-testid="studio-contact-body">
          <p className="text-[12px] leading-relaxed mb-3" style={{ color: "#475569" }}>
            These fields render at the top of every downloaded résumé. Defaults come from
            your account. Edits are saved for next time.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <ContactField label="Full name" icon={User} value={contact.name}
                          onChange={set("name")} placeholder="Jane Q. Applicant"
                          testid="studio-contact-name" />
            <ContactField label="Email" icon={Mail} value={contact.email}
                          onChange={set("email")} placeholder="jane@example.com"
                          testid="studio-contact-email" />
            <ContactField label="Phone" icon={Phone} value={contact.phone}
                          onChange={set("phone")} placeholder="303-555-0100"
                          testid="studio-contact-phone" />
            <ContactField label="Location" icon={MapPin} value={contact.location}
                          onChange={set("location")} placeholder="Denver, CO"
                          testid="studio-contact-location" />
            <div className="md:col-span-2">
              <ContactField label="LinkedIn" icon={Linkedin} value={contact.linkedin_url}
                            onChange={set("linkedin_url")}
                            placeholder="linkedin.com/in/janeqa"
                            testid="studio-contact-linkedin" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                  style={{ color: saveStatus === "saved" ? "#0E5A4A" : "#94A3B8" }}
                  data-testid="studio-contact-save-status">
              {saveStatus === "saving" ? "· Saving…"
                : saveStatus === "saved" ? "· Saved ✓"
                : ""}
            </span>
            <button
              type="button"
              onClick={onSave}
              disabled={saveStatus === "saving"}
              className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-[11px] font-mono-rm uppercase tracking-widest"
              style={{
                background: "#0F1B2D", color: "#FFC145",
                border: "1.5px solid #0F1B2D", fontWeight: 700,
                cursor: saveStatus === "saving" ? "wait" : "pointer",
              }}
              data-testid="studio-contact-save"
            >
              <Save size={11} /> Save for next time
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactField({ label, icon: Icon, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-0.5"
           style={{ color: "#475569", fontWeight: 700 }}>· {label}</div>
      <div className="relative">
        {Icon && (
          <Icon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: "#94A3B8" }} aria-hidden />
        )}
        <input
          type="text" value={value || ""} onChange={onChange}
          placeholder={placeholder}
          className="w-full rounded px-2 py-1.5 text-[12.5px]"
          style={{
            border: "1.5px solid #CBD5E1", background: "#F8FAFC",
            paddingLeft: Icon ? "1.9rem" : undefined,
          }}
          data-testid={testid}
        />
      </div>
    </label>
  );
}


